import React from 'react'
import PropTypes from 'prop-types'

import { Modal, ModalHeader, ModalSubHeader, ModalContent } from '@components/Modal'
import { Button } from '@components/Button'

import {
  OverallCalculationStrategy,
  OverallCalculationStrategyLabels,
  overallCalculationStrategyOptionPropTypes
} from './CalculationConfig.helpers'

const LearnMoreModal = ({
  className,
  open,
  onClose,
  calculationStrategy
}) => {
  const calculationStrategyLabel = OverallCalculationStrategyLabels[calculationStrategy].abbreviated
  const automaticLabel = OverallCalculationStrategyLabels[OverallCalculationStrategy.automatic].abbreviated
  const hybridLabel = OverallCalculationStrategyLabels[OverallCalculationStrategy.hybrid].abbreviated
  const customLabel = OverallCalculationStrategyLabels[OverallCalculationStrategy.custom].abbreviated
  const labelsList = <>
    <code>{automaticLabel}</code>, <code>{hybridLabel}</code>, and <code>{customLabel}</code>
  </>
  const closeModalAndScrollToAssets = () => {
    onClose()
    const assetsUI = document.querySelector('.ui-card.ui-card--asset')
    if (assetsUI) {
      assetsUI.scrollIntoView({ behavior: 'smooth' })
    } else {
      Sentry.captureMessage('Asset UI card not found when attempting to scroll it into view from the calculation config modal')
    }
  }
  const getCalculationStrategyExplanation = (calculationStrategy) => {
    switch (calculationStrategy) {
      case OverallCalculationStrategy.automatic:
        return 'because no assets are set to a manual withdrawal value neither before or after retirement.'
      case OverallCalculationStrategy.hybrid:
        return `because at least one asset is set to a manual withdrawal value either before or after retirement.`
      case OverallCalculationStrategy.custom:
        return 'because all assets are set to a manual withdrawal value either before or after retirement.'
      default:
        throw new Error(`No explanation exists for calculation strategy: ${calculationStrategy}`)
    }
  }
  const calcStrategyExplanation = getCalculationStrategyExplanation(calculationStrategy)

  return (
    <Modal open={open} onClose={onClose} className={className}>
      <ModalHeader>Calculation Settings</ModalHeader>

      <ModalSubHeader>
        Learn more about Wealthbit calculations
      </ModalSubHeader>

      <ModalContent>
        <p>The overall calculation strategy for this scenario is <code>{calculationStrategyLabel}</code>, {calcStrategyExplanation}</p>
        <p>There are 3 broad calculation strategies at Wealthbit with varying levels of automation: {labelsList}.</p>

        <details><summary>Learn about each strategy</summary>
          <ul>
            <li>Automatic is fully automatic and allows Wealthbit to make draw down decisions when calculating a projection. This is the system default and it will withdraw more from an asset than is specified as monthly withdrawal income in order to satisfy budget requirements both before and after retirement. Draws are always constrained by legislative rules, for example the Living Annuity draw rate cap.</li>
            <li>Hybrid is when draw down for at least one asset is controlled manually. For this asset the calculations will respect the monthly draw amount set and <i>will not exceed it</i> to satisfy budget requirements.</li>
            <li>Custom has no draw automation and the calculation engine will not exceed the specified draw on any asset before or after retirement, giving you even more control over asset draws.</li>
          </ul>
        </details>

        <p>To change the calculation strategy, navigate to edit any asset with a non-zero withdrawal specified and toggle the checkbox to control this behaviour</p>
      </ModalContent>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
        <Button secondary onClick={onClose}>Close</Button>
        <Button onClick={closeModalAndScrollToAssets}>Go to assets</Button>
      </div>
    </Modal>
  )
}

LearnMoreModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  calculationStrategy: overallCalculationStrategyOptionPropTypes.isRequired,
}

export { LearnMoreModal }
